import type {AnimationClip, Group, WebGLRenderer} from 'three'
import {GLTFLoader} from 'three/addons/loaders/GLTFLoader.js'

import {AppError} from '../util/errors'

/**
 * Self-hosted by scripts/vendor-engine.mjs. Never a third-party CDN.
 *
 * The object form of `setDecoderPath` selects three's glTF-specific Draco build
 * (`libs/draco/gltf/`) over the general-purpose one: 192 KB of wasm instead of
 * 285 KB, for the same result on mesh data. It also sets the loader's `dep_js`
 * to null, so the 719 KB asm.js fallback can no longer be reached - acceptable
 * because the XR engine itself requires WebAssembly, so any device that gets
 * this far has it.
 */
const DRACO_DECODER_PATHS = {
  js: '/decoders/draco/gltf/draco_wasm_wrapper.js',
  wasm: '/decoders/draco/gltf/draco_decoder.wasm',
}
const BASIS_TRANSCODER_PATH = '/decoders/basis/'

const GLB_MAGIC = 0x46546c67 // 'glTF' little-endian
const GLB_CHUNK_JSON = 0x4e4f534a // 'JSON' little-endian
const GLB_HEADER_BYTES = 12

export interface LoadedGltf {
  readonly scene: Group
  readonly animations: readonly AnimationClip[]
}

/**
 * Reads the glTF JSON chunk's extension lists without decoding the whole file.
 *
 * This is what lets us register decoders on demand. Registering all three up
 * front would be simpler, but DRACOLoader + KTX2Loader + MeshoptDecoder add
 * ~46 KB gzipped to the main bundle and make Vite emit ~1.5 MB of decoder
 * assets — a real cost on the critical path of a mobile AR experience, paid on
 * every load, for a capability most models never use.
 */
const readExtensions = (bytes: ArrayBuffer): readonly string[] => {
  const view = new DataView(bytes)
  if (view.byteLength < GLB_HEADER_BYTES + 8) return []

  const chunkLength = view.getUint32(GLB_HEADER_BYTES, true)
  const chunkType = view.getUint32(GLB_HEADER_BYTES + 4, true)
  if (chunkType !== GLB_CHUNK_JSON) return []

  const start = GLB_HEADER_BYTES + 8
  const end = Math.min(start + chunkLength, view.byteLength)

  try {
    const json: unknown = JSON.parse(new TextDecoder().decode(bytes.slice(start, end)))
    if (typeof json !== 'object' || json === null) return []
    const record = json as Record<string, unknown>
    const used = record['extensionsUsed']
    const required = record['extensionsRequired']
    return [...(Array.isArray(used) ? used : []), ...(Array.isArray(required) ? required : [])]
      .filter((value): value is string => typeof value === 'string')
  } catch {
    // A malformed JSON chunk is GLTFLoader's problem to report, not ours.
    return []
  }
}

/**
 * Attaches only the decoders this particular file actually needs.
 *
 * `GLTFLoader` throws if compressed data arrives without its decoder already
 * registered, and we do not control how the next campaign's GLB was exported -
 * so the capability has to be there. Gating it on the file's own extension list
 * keeps that robustness without taxing the uncompressed case: each decoder is a
 * dynamic import, so it becomes its own chunk and is fetched only on demand.
 */
const attachDecoders = async (
  loader: GLTFLoader,
  renderer: WebGLRenderer,
  extensions: readonly string[]
): Promise<readonly string[]> => {
  const attached: string[] = []

  if (extensions.includes('KHR_draco_mesh_compression')) {
    const {DRACOLoader} = await import('three/addons/loaders/DRACOLoader.js')
    const draco = new DRACOLoader()
    // Explicit, self-hosted paths. r185 defaults to an `import.meta.url`-relative
    // location which probably resolves under Vite, but explicit paths are
    // greppable and can never silently point at a third-party origin.
    draco.setDecoderPath(DRACO_DECODER_PATHS)
    loader.setDRACOLoader(draco)
    attached.push('draco')
  }

  if (extensions.includes('KHR_texture_basisu')) {
    const {KTX2Loader} = await import('three/addons/loaders/KTX2Loader.js')
    const ktx2 = new KTX2Loader()
    ktx2.setTranscoderPath(BASIS_TRANSCODER_PATH)
    ktx2.detectSupport(renderer)
    loader.setKTX2Loader(ktx2)
    attached.push('ktx2')
  }

  if (
    extensions.includes('EXT_meshopt_compression') ||
    extensions.includes('KHR_meshopt_compression')
  ) {
    const {MeshoptDecoder} = await import('three/addons/libs/meshopt_decoder.module.js')
    loader.setMeshoptDecoder(MeshoptDecoder)
    attached.push('meshopt')
  }

  return attached
}

export interface LoadModelResult extends LoadedGltf {
  /** Which decoders were needed. Surfaced for the ?debug=1 panel. */
  readonly decoders: readonly string[]
  readonly byteLength: number
}

/**
 * Fetches the model as bytes and parses it.
 *
 * The fetch is done by hand rather than via `loader.loadAsync(url)` so a typo
 * in `campaign.json` produces an actionable Japanese message instead of an
 * opaque parse failure deep inside GLTFLoader. This is the most likely thing to
 * go wrong during a model swap, so it gets the clearest error.
 */
export const loadModel = async (
  renderer: WebGLRenderer,
  url: string,
  signal?: AbortSignal
): Promise<LoadModelResult> => {
  let response: Response
  try {
    response = await fetch(url, signal ? {signal} : undefined)
  } catch (cause) {
    throw new AppError('MODEL_FETCH_FAILED', `fetch ${url} failed`, {
      cause,
      userMessageJa: 'モデルを読み込めませんでした。通信環境を確認してね。',
    })
  }

  if (!response.ok) {
    throw new AppError('MODEL_FETCH_FAILED', `${url} returned ${response.status}`, {
      userMessageJa: `モデルが見つかりません (${response.status})。campaign.json の model.url を確認してね。`,
    })
  }

  // Workers is configured with `not_found_handling: "none"`, so a missing file
  // is a real 404. This guard catches the other shape of the same mistake: a
  // path that resolves to an HTML page.
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('text/html')) {
    throw new AppError('MODEL_FETCH_FAILED', `${url} served ${contentType}`, {
      userMessageJa:
        'モデルの代わりにHTMLが返ってきました。campaign.json の model.url のパスを確認してね。',
    })
  }

  const bytes = await response.arrayBuffer()

  if (bytes.byteLength < GLB_HEADER_BYTES || new DataView(bytes).getUint32(0, true) !== GLB_MAGIC) {
    throw new AppError('MODEL_PARSE_FAILED', `${url} is not a binary glTF`, {
      userMessageJa: 'GLBファイルではないみたいです。書き出し形式を確認してね。',
    })
  }

  const loader = new GLTFLoader()
  const decoders = await attachDecoders(loader, renderer, readExtensions(bytes))

  const gltf = await new Promise<LoadedGltf>((resolve, reject) => {
    loader.parse(
      bytes,
      // Resource path for any external .bin/textures. A .glb is self-contained,
      // but a sibling-relative path keeps a stray .gltf working too.
      url.slice(0, url.lastIndexOf('/') + 1),
      (result) => resolve({scene: result.scene, animations: result.animations}),
      (cause) =>
        reject(
          new AppError('MODEL_PARSE_FAILED', `GLTFLoader failed on ${url}`, {
            cause,
            userMessageJa: 'モデルの読み込みに失敗しました。ファイルが壊れていないか確認してね。',
          })
        )
    )
  })

  return {...gltf, decoders, byteLength: bytes.byteLength}
}
